import { DATE_FORMATS, formatDate } from '@amelio/ui-library';
import { useMemo } from 'react';

import { buildGuardEmail } from './launchChanges/buildGuardEmail';
import {
    communicationBlockByStepId,
    communicationTypeForStep,
    readStepEmails,
} from './launchChanges/launchStepSources';
import {
    evaluateLaunchGuard,
    type LaunchGuardMilestone,
    type LaunchGuardQuestionnaire,
} from './launchGuard';
import { formatMilestoneSentOn } from './launchSummaryFormatting';

import type {
    EvaluationSummaryRow,
    ScheduleSummaryRow,
} from './components/LaunchSummary/LaunchSummary';
import type { StepEmails } from './launchChanges/launchStepSources';
import type { useLaunchSummaryQueries } from './useLaunchSummaryQueries';
import type { useAmelioTranslations } from '@/hooks';

import { isCycleEvaluationGroupReadOnly } from '@/navigation/CycleEvaluation/cycleEvaluationGroupReadOnly';
import {
    DeadlineMilestoneId,
    deriveDueDate,
    getVisibleDeadlineMilestones,
    isLaunchMilestone,
    LAUNCH_MILESTONE_BY_TYPE,
} from '@/pages/CycleEvaluation/Deadlines/milestones';
import { CycleEvaluationGroupStatus } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import { toCalendarLocale } from '@/utils/dateUtils';
import { getLocalizedText } from '@/utils/localization';

type LaunchSummaryQueriesResult = ReturnType<typeof useLaunchSummaryQueries>;

export interface UseLaunchGuardViewParams {
    group: LaunchSummaryQueriesResult['group'];
    orphanMilestones: LaunchSummaryQueriesResult['orphanMilestones'];
    checkedTypes: LaunchSummaryQueriesResult['checkedTypes'];
    cycleEvaluationByType: LaunchSummaryQueriesResult['cycleEvaluationByType'];
    hasQuestionnaireContentByType: LaunchSummaryQueriesResult['hasQuestionnaireContentByType'];
    locale: string;
    t: ReturnType<typeof useAmelioTranslations>['t'];
}

export const useLaunchGuardView = ({
    group,
    orphanMilestones,
    checkedTypes,
    cycleEvaluationByType,
    hasQuestionnaireContentByType,
    locale,
    t,
}: UseLaunchGuardViewParams) => {
    const calendarLocale = toCalendarLocale(locale);

    const evaluations: EvaluationSummaryRow[] = useMemo(
        () =>
            checkedTypes.map((type) => {
                const cycleEvaluation = cycleEvaluationByType[type];
                const template = cycleEvaluation?.cycleQuestionnaire?.reviewTemplate?.title;
                const templateLabel = template
                    ? getLocalizedText(template)
                    : t('cycleEvaluationSchedule.launchSummary.evaluations.noTemplate');

                return {
                    id: type,
                    labelKey: LAUNCH_MILESTONE_BY_TYPE[type].labelKey,
                    templateLabel,
                    isBlocked: !hasQuestionnaireContentByType[type],
                };
            }),
        [checkedTypes, cycleEvaluationByType, hasQuestionnaireContentByType, t],
    );

    const guardQuestionnaires: LaunchGuardQuestionnaire[] = useMemo(
        () =>
            checkedTypes.map((type) => ({
                id: type,
                labelKey: LAUNCH_MILESTONE_BY_TYPE[type].labelKey,
                hasQuestionnaireContent: !!hasQuestionnaireContentByType[type],
            })),
        [checkedTypes, hasQuestionnaireContentByType],
    );

    const visibleMilestones = useMemo(
        () => getVisibleDeadlineMilestones(checkedTypes),
        [checkedTypes],
    );

    const emailsByMilestoneId = useMemo(() => {
        const communicationByType = new Map(
            (group?.communications ?? []).map((block) => [block.milestoneType, block]),
        );

        return new Map<string, StepEmails>(
            visibleMilestones.map((milestone) => {
                const definition = communicationBlockByStepId.get(milestone.id);

                if (!definition) {
                    return [milestone.id, {}];
                }

                return [
                    milestone.id,
                    readStepEmails({
                        definition,
                        cycleEvaluation: isLaunchMilestone(milestone)
                            ? cycleEvaluationByType[milestone.evaluationType]
                            : undefined,
                        block: communicationByType.get(communicationTypeForStep(definition)),
                    }),
                ];
            }),
        );
    }, [group?.communications, visibleMilestones, cycleEvaluationByType]);

    const guardMilestones: LaunchGuardMilestone[] = useMemo(
        () =>
            visibleMilestones.map((milestone) => {
                if (isLaunchMilestone(milestone)) {
                    const cycleEvaluation = cycleEvaluationByType[milestone.evaluationType];
                    const schedule = cycleEvaluation?.cycleSchedule;

                    const scheduledAt = schedule?.scheduledAt
                        ? new Date(schedule.scheduledAt)
                        : undefined;
                    const dueAt =
                        schedule && scheduledAt
                            ? deriveDueDate({
                                  scheduledAt,
                                  daysAllowedToComplete: schedule.daysAllowedToComplete,
                              })
                            : undefined;

                    return {
                        id: milestone.id,
                        labelKey: milestone.labelKey,
                        scheduledAt,
                        dueAt,
                        email: buildGuardEmail(emailsByMilestoneId.get(milestone.id)),
                    };
                }

                const existing = orphanMilestones.find(
                    (row) => row.milestoneType === milestone.milestoneType,
                );

                return {
                    id: milestone.id,
                    labelKey: milestone.labelKey,
                    scheduledAt: existing?.scheduledAt ? new Date(existing.scheduledAt) : undefined,
                    isClosure: milestone.id === DeadlineMilestoneId.EVALUATION_CYCLE_CLOSURE,
                };
            }),
        [visibleMilestones, cycleEvaluationByType, orphanMilestones, emailsByMilestoneId],
    );

    const isConfigurationLocked = isCycleEvaluationGroupReadOnly(group);

    const guardResult = useMemo(
        () =>
            evaluateLaunchGuard({
                milestones: guardMilestones,
                questionnaires: guardQuestionnaires,
                isConfigurationLocked,
            }),
        [guardMilestones, guardQuestionnaires, isConfigurationLocked],
    );
    const blockedMilestoneIds = useMemo(
        () => new Set(guardResult.blockedMilestones.map((milestone) => milestone.id)),
        [guardResult],
    );

    // Read from the switches themselves, never from the matrix of which cards a step OWNS: a step
    // that has an employee card the administrator turned off sends nothing to employees.
    const scheduleRows: ScheduleSummaryRow[] = useMemo(
        () =>
            guardMilestones.map((milestone) => {
                const emails = emailsByMilestoneId.get(milestone.id);
                const audienceLabels: string[] = [];

                if (emails?.employeeEmail?.isEnabled) {
                    audienceLabels.push(
                        t('cycleEvaluationSchedule.launchSummary.schedule.audienceEmployees'),
                    );
                }

                if (emails?.managerEmail?.isEnabled) {
                    audienceLabels.push(
                        t('cycleEvaluationSchedule.launchSummary.schedule.audienceManagers'),
                    );
                }

                return {
                    id: milestone.id,
                    labelKey: milestone.labelKey,
                    dateTimeLabel: formatMilestoneSentOn(milestone.scheduledAt, calendarLocale, t),
                    audienceLabels,
                    isBlocked: blockedMilestoneIds.has(milestone.id),
                };
            }),
        [guardMilestones, emailsByMilestoneId, blockedMilestoneIds, calendarLocale, t],
    );

    const armedCount = useMemo(
        () => guardMilestones.filter((milestone) => !!milestone.scheduledAt).length,
        [guardMilestones],
    );

    const periodLabel = useMemo(() => {
        if (!group?.evaluationPeriodStart || !group?.evaluationPeriodEnd) {
            return '—';
        }

        const startLabel = formatDate({
            date: group.evaluationPeriodStart,
            format: DATE_FORMATS.SHORT_DATE,
            language: locale,
            timezone: 'UTC',
        });
        const endLabel = formatDate({
            date: group.evaluationPeriodEnd,
            format: DATE_FORMATS.SHORT_DATE,
            language: locale,
            timezone: 'UTC',
        });

        return `${startLabel} → ${endLabel}`;
    }, [group?.evaluationPeriodStart, group?.evaluationPeriodEnd, locale]);

    const isAlreadyLaunched = group?.status === CycleEvaluationGroupStatus.ACTIVE;

    return { evaluations, guardResult, scheduleRows, armedCount, periodLabel, isAlreadyLaunched };
};
